import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getUserData, UserData } from "../models/user";
import posthog from "posthog-js";

let lastCapturedUserId: string | null = null;

/**
 * Отримує start_param з Telegram WebApp (якщо є).
 * Джерела (у пріоритеті): initDataUnsafe.start_param → query → hash.
 * Telegram Mini Apps часто кладуть `tgWebAppStartParam` лише в hash fragment;
 * ранній `return null` без WebApp ламав deep-link кнопки з повідомлень.
 */
export const getTelegramStartParam = (): string | null => {
  try {
    const fromUnsafe = window.Telegram?.WebApp?.initDataUnsafe?.start_param;
    if (fromUnsafe) return fromUnsafe;

    const fromSearch = new URLSearchParams(window.location.search).get("tgWebAppStartParam");
    if (fromSearch) return fromSearch;

    const hash = window.location.hash.startsWith("#")
      ? window.location.hash.slice(1)
      : window.location.hash;
    if (hash) {
      const fromHash = new URLSearchParams(hash).get("tgWebAppStartParam");
      if (fromHash) return fromHash;
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Хук для отримання даних користувача (один раз за всю сесію, кешується)
 * Всі компоненти використовують той самий кешований об'єкт userData
 * Ендпоїнт завжди обраховує найактуальніший стан користувача
 */
export function useUserData() {
  const query = useQuery<UserData>({
    queryKey: ["userData"],
    queryFn: getUserData,
    retry: 3,
    staleTime: 10 * 60 * 1000, // 10 хвилин
    gcTime: Infinity, // Кеш ніколи не видаляється
    refetchOnMount: false, // Не робити refetch при монтуванні (оновлення лише після купівлі VIP)
    refetchOnWindowFocus: false, // Не робити refetch при фокусі вікна
    refetchOnReconnect: false, // Не робити refetch при переподключенні
  });

  useEffect(() => {
    const userData = query.data;
    if (!userData?.user_id) return;
    if (lastCapturedUserId === userData.user_id) return;

    const isVip = Boolean(userData.vip_until);
    const startParam = getTelegramStartParam();

    posthog.capture('game_session_started', {
      is_vip: isVip,
      ...(startParam ? { start_param: startParam } : {}),
    });
    lastCapturedUserId = userData.user_id;
  }, [query.data]);

  return query;
}

